import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useReactiveVar } from '@apollo/client';
import { Crop } from 'react-image-crop';
import { toast } from 'react-toastify';

import * as E from '@pages/Equipment/Equipment.style';
import theme from '@styles/theme';
import { mediaListHeader } from '@lib/data';
import { didInfoVar } from '@store/index';
import { PLAYLIST_DATA } from '@lib/DUMMY';
import useFileUpload from '@hooks/useFileUpload';

import { RegularFont } from '@components/styled/StyledComponents';
import TableHeader from '@components/share/Table/TableHeader';
import { EmptyTable, TableBody } from '@components/share/Table/table.style';
import { InfoSection } from '@components/share/Info/InfoSection';
import StyledButton from '@components/styled/StyledButton';
import StyledFileUpload from '@components/styled/StyledFileUpload';
import MediaListRow from './MediaListRow';
import ImageCropModal from '@components/share/Modal/Crop/ImageCropModal';

const ImageSetting = () => {
    const { version, type } = useReactiveVar(didInfoVar);
    const [openModal, setOpenModal] = useState(false);
    const [images, setImages] = useState<Record<string, boolean>>({});
    const [imageType, setImageType] = useState('');
    const [aspect, setAspect] = useState<number>(16 / 9);

    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState('');
    const { file, setFile, previewUrl, setPreviewUrl, onChangeFile } =
        useFileUpload();

    useEffect(() => {
        if (version === 1) {
            if (type === 3) {
                setImageType('정방형');
                setAspect(1);
            } else if (type === 4) {
                setImageType('전면');
                setAspect(9 / 16);
            } else {
                setImageType('배너');
                setAspect(16 / 9);
            }
        } else if (version === 2) {
            setImageType('전면');
            setAspect(9 / 16);
        } else if (version === 3) {
            setImageType('정방형');
            setAspect(1);
        }
    }, [version, type]);

    const handleRadioButtons = useCallback(
        (radioKey: string, value: number) => {
            if (
                !value &&
                !window.confirm(
                    '사용중인 이미지를 미사용으로 변경하시는 경우 플레이리스트에서 즉시 삭제 됩니다. 미사용으로 변경하시겠어요?'
                )
            ) {
                return;
            }
            // TODO: 추후 리졸버 적용
            try {
                setImages((prev) => {
                    return {
                        ...prev,
                        [radioKey]: !!value
                    };
                });
                toast.success(
                    `이미지가 ${
                        value ? '사용중' : '미사용'
                    }으로 변경되었습니다.`
                );
            } catch (err) {
                console.error(err);
            }
        },
        []
    );

    const onDelete = (id: number) => {
        if (
            !window.confirm(
                '이미지를 삭제하시면 복구가 불가능합니다. 정말 삭제하시겠어요?'
            )
        )
            return;
        try {
            toast.success('파일을 삭제했습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    const onSubmit = () => {
        if (PLAYLIST_DATA.length !== Object.keys(images).length)
            return toast.warning('사용 여부를 전부 선택해주세요.');
        try {
            toast.success('저장되었습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <InfoSection title={`${imageType} 이미지 설정`} ROW>
                <StyledFileUpload
                    title="이미지 추가"
                    subTitle="(jpg / png 만 가능)"
                    amountTitle="남은 용량"
                    amountCount={`(${200}MB / ${300}MB)`}
                    width={630}
                    margin="0 0 24px"
                    onChange={(e) => {
                        onChangeFile(e);
                        setOpenModal(true);
                    }}
                />
                <RegularFont
                    $margin="0 0 12px"
                    $fontColor={theme.colors.positiveBlue}
                >
                    플레이리스트에는 사용중인 이미지만 노출됩니다.
                </RegularFont>
                <E.SectionWrapper $width="630px">
                    <TableHeader
                        header={mediaListHeader}
                        bgColor={theme.colors.ultraLightGrayBorder}
                    />
                    <TableBody>
                        {PLAYLIST_DATA.length ? (
                            PLAYLIST_DATA.map((data, index) => {
                                return (
                                    <MediaListRow
                                        key={`${index}-image-list`}
                                        header={mediaListHeader}
                                        data={data}
                                        onChange={handleRadioButtons}
                                        onDelete={onDelete}
                                        radioGroup={images}
                                        radioKey={`image${data.id}`}
                                        imgWidth={aspect > 1 ? 45 : 24}
                                        imgHeight={aspect >= 1 ? 24 : 45}
                                    />
                                );
                            })
                        ) : (
                            <EmptyTable>
                                재생할 플레이리스트가 없습니다.
                            </EmptyTable>
                        )}
                    </TableBody>
                </E.SectionWrapper>
            </InfoSection>
            {openModal && (
                <ImageCropModal
                    file={file}
                    imageCropModal={openModal}
                    setImageCropModal={setOpenModal}
                    previewUrl={previewUrl}
                    setMainImage={setImage}
                    setMainPreview={setImagePreview}
                    aspect={aspect}
                />
            )}
        </>
    );
};

export default ImageSetting;
